import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Filter, Plus, Eye, CheckCircle2, XCircle, Users, ShieldCheck, RefreshCw, AlertCircle, Mail } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useHosts, useApproveHost, useRejectHost, useValidateRegistration } from "@/hooks/useHosts";
import { useRegistrationCertificate, useUpdateDocumentStatus, REGISTRATION_CERT_NAME } from "@/hooks/useDocuments";
import { useUnits } from "@/hooks/useUnits";
import { useSendHostInvitation, useOrgInvitations, useRevokeInvitation } from "@/hooks/useInvitations";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

export default function Hosts() {
  const navigate = useNavigate();
  const { activeOrgId } = useAuth();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewHost, setViewHost] = useState<any>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", unitId: "", nightLimit: "90" });

  const { data: hosts = [], isLoading, error } = useHosts();
  const { data: units = [] } = useUnits();
  const { data: pendingInvites = [] } = useOrgInvitations(activeOrgId);
  const approveHost = useApproveHost();
  const rejectHost = useRejectHost();
  const validateRegistration = useValidateRegistration();
  const { data: viewHostCert, refetch: refetchViewCert } = useRegistrationCertificate(viewHost?.id);
  const updateDocStatus = useUpdateDocumentStatus();
  const sendHostInvitation = useSendHostInvitation();
  const revokeInvitation = useRevokeInvitation();

  // Units that don't already have a host or a pending invite
  const invitedUnitIds = new Set(
    pendingInvites.filter((i: any) => i.role === "host" && i.unit_id).map((i: any) => i.unit_id)
  );
  const existingHostUnitIds = new Set(hosts.map((h: any) => h.unit_id));
  const availableUnitsForInvite = units.filter(
    (u: any) => !existingHostUnitIds.has(u.id) && !invitedUnitIds.has(u.id)
  );

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteForm.email || !inviteForm.unitId) return;
    const selectedUnit = units.find((u: any) => u.id === inviteForm.unitId);
    try {
      await sendHostInvitation.mutateAsync({
        email: inviteForm.email,
        unitId: inviteForm.unitId,
        unitNumber: selectedUnit?.unit_number ?? "",
        nightLimit: parseInt(inviteForm.nightLimit) || 90,
      });
      toast({
        title: "Invitation sent",
        description: `Host invitation sent to ${inviteForm.email} for Unit ${selectedUnit?.unit_number}.`,
      });
      setInviteForm({ email: "", unitId: "", nightLimit: "90" });
      setInviteOpen(false);
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const filtered = hosts.filter((h: any) => {
    const matchesSearch =
      (h.profile?.full_name || "").toLowerCase().includes(search.toLowerCase()) ||
      (h.unit?.unit_number || "").includes(search) ||
      (h.registration_number || "").toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || h.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleVerify = (host: any) => {
    if (!host.registration_number) return;
    validateRegistration.mutate({
      hostId: host.id,
      registrationNumber: host.registration_number,
      hostName: host.profile?.full_name,
      unitAddress: host.unit?.unit_number ? `Unit ${host.unit.unit_number}` : undefined,
    });
  };

  if (error) {
    return (
      <div className="flex items-center justify-center p-12">
        <p className="text-sm text-destructive">Failed to load hosts. Please try again.</p>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Host Management</h1>
            <p className="text-sm text-muted-foreground">{hosts.length} registered hosts</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setInviteOpen(true)}>
              <Mail className="mr-1 h-4 w-4" /> Invite Host
            </Button>
            <Button size="sm" onClick={() => navigate("/hosts/add")}>
              <Plus className="mr-1 h-4 w-4" /> Add Host
            </Button>
          </div>
        </div>

        {/* Pending Host Invitations */}
        {pendingInvites.filter((i: any) => i.role === "host").length > 0 && (
          <Card>
            <CardHeader className="pb-2 pt-4 px-4">
              <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" />
                Pending Host Invitations ({pendingInvites.filter((i: any) => i.role === "host").length})
              </p>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="flex flex-wrap gap-2">
                {pendingInvites
                  .filter((i: any) => i.role === "host")
                  .map((inv: any) => (
                    <div
                      key={inv.id}
                      className="flex items-center gap-2 rounded-md border border-dashed px-3 py-1.5 text-xs"
                    >
                      <span className="font-medium">{inv.email}</span>
                      {inv.unit_id && (
                        <Badge variant="outline" className="text-[10px]">
                          Unit {units.find((u: any) => u.id === inv.unit_id)?.unit_number ?? "—"}
                        </Badge>
                      )}
                      <span className="text-muted-foreground">
                        expires {new Date(inv.expires_at).toLocaleDateString()}
                      </span>
                      <button
                        className="text-destructive hover:text-destructive/80 ml-1"
                        title="Revoke invitation"
                        onClick={() => revokeInvitation.mutate(inv.id)}
                      >
                        <XCircle className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input placeholder="Search by name, unit, or registration..." className="pl-9 h-9" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-9 w-[140px] text-xs"><Filter className="mr-1 h-3 w-3" /><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Users className="h-10 w-10 text-muted-foreground/50 mb-3" />
                <p className="text-sm font-medium text-muted-foreground">No hosts found</p>
                <p className="text-xs text-muted-foreground">
                  {search || statusFilter !== "all" ? "Try adjusting your filters." : "No hosts registered yet."}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Host</TableHead>
                    <TableHead className="text-xs">Unit</TableHead>
                    <TableHead className="text-xs">Registration #</TableHead>
                    <TableHead className="text-xs">Registry</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs">Expires</TableHead>
                    <TableHead className="text-xs">Nights Used</TableHead>
                    <TableHead className="text-xs w-[130px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((host: any) => {
                    const isVerifying = validateRegistration.isPending && validateRegistration.variables?.hostId === host.id;

                    return (
                      <TableRow key={host.id}>
                        <TableCell>
                          <div>
                            <p className="text-sm font-medium">{host.profile?.full_name || "Unknown"}</p>
                            <p className="text-xs text-muted-foreground">{host.profile?.email || ""}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{host.unit?.unit_number || "—"}</TableCell>
                        <TableCell>
                          {host.registration_number
                            ? <Badge variant="outline" className="text-[10px] font-mono">{host.registration_number}</Badge>
                            : <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>

                        {/* Registry Status Column */}
                        <TableCell>
                          {host.registration_number ? (
                            host.status === "approved" ? (
                              <div className="flex items-center gap-1">
                                <ShieldCheck className="h-3.5 w-3.5 text-success" />
                                <span className="text-xs text-success font-medium">Verified</span>
                              </div>
                            ) : host.status === "expired" ? (
                              <div className="flex items-center gap-1">
                                <AlertCircle className="h-3.5 w-3.5 text-destructive" />
                                <span className="text-xs text-destructive font-medium">Expired</span>
                              </div>
                            ) : host.status === "rejected" ? (
                              <div className="flex items-center gap-1">
                                <XCircle className="h-3.5 w-3.5 text-destructive" />
                                <span className="text-xs text-destructive font-medium">Invalid</span>
                              </div>
                            ) : host.status === "suspended" ? (
                              <div className="flex items-center gap-1">
                                <XCircle className="h-3.5 w-3.5 text-destructive" />
                                <span className="text-xs text-destructive font-medium">Suspended</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1">
                                <AlertCircle className="h-3.5 w-3.5 text-warning" />
                                <span className="text-xs text-warning font-medium">Unverified</span>
                              </div>
                            )
                          ) : (
                            <span className="text-xs text-muted-foreground">No Reg#</span>
                          )}
                        </TableCell>

                        <TableCell><StatusBadge status={host.status || "pending"} /></TableCell>
                        <TableCell className="text-xs">
                          {host.expires_at ? new Date(host.expires_at).toLocaleDateString() : "—"}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{host.nights_used ?? 0}</span>
                            <span className="text-xs text-muted-foreground">/ {host.night_limit ?? 90}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="View host details" onClick={() => setViewHost(host)}>
                                  <Eye className="h-3 w-3" aria-hidden="true" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>View details</TooltipContent>
                            </Tooltip>

                            {/* Registry Verify Button */}
                            {host.registration_number && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-blue-500"
                                    aria-label="Verify registration"
                                    onClick={() => handleVerify(host)}
                                    disabled={isVerifying}
                                  >
                                    <RefreshCw className={`h-3 w-3 ${isVerifying ? "animate-spin" : ""}`} aria-hidden="true" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Verify Registration</TooltipContent>
                              </Tooltip>
                            )}

                            {host.status === "pending" && (
                              <>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-success" aria-label="Approve host" onClick={() => approveHost.mutate(host.id)} disabled={approveHost.isPending}>
                                      <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Approve host</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" aria-label="Reject host" onClick={() => rejectHost.mutate(host.id)} disabled={rejectHost.isPending}>
                                      <XCircle className="h-3 w-3" aria-hidden="true" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Reject host</TooltipContent>
                                </Tooltip>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Invite Host Dialog */}
        <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Invite Host by Email</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSendInvite} className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="invite_email">Host Email Address *</Label>
                <Input
                  id="invite_email"
                  type="email"
                  placeholder="host@example.com"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite_unit">Assign Unit *</Label>
                {availableUnitsForInvite.length === 0 ? (
                  <p className="text-sm text-muted-foreground rounded-md border border-dashed p-3 text-center">
                    All units already have a host or a pending invitation.
                  </p>
                ) : (
                  <Select
                    value={inviteForm.unitId}
                    onValueChange={(v) => setInviteForm((f) => ({ ...f, unitId: v }))}
                    required
                  >
                    <SelectTrigger id="invite_unit">
                      <SelectValue placeholder="Select a unit..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableUnitsForInvite.map((u: any) => (
                        <SelectItem key={u.id} value={u.id}>
                          Unit {u.unit_number} — {u.owner_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite_night_limit">Night Limit (per year)</Label>
                <Input
                  id="invite_night_limit"
                  type="number"
                  min={1}
                  max={365}
                  value={inviteForm.nightLimit}
                  onChange={(e) => setInviteForm((f) => ({ ...f, nightLimit: e.target.value }))}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                The host will receive an email with a link to accept the invitation and create their account.
                The link expires in 7 days.
              </p>
              <DialogFooter>
                <Button variant="outline" type="button" onClick={() => setInviteOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={sendHostInvitation.isPending || !inviteForm.email || !inviteForm.unitId}
                >
                  {sendHostInvitation.isPending ? "Sending..." : "Send Invitation"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Host Detail Dialog */}
        <Dialog open={!!viewHost} onOpenChange={() => setViewHost(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Host Details</DialogTitle>
            </DialogHeader>
            {viewHost && (
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Name</Label>
                    <p className="text-sm font-medium">{viewHost.profile?.full_name || "—"}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Email</Label>
                    <p className="text-sm">{viewHost.profile?.email || "—"}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Unit</Label>
                    <p className="text-sm font-medium">Unit {viewHost.unit?.unit_number || "—"}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Status</Label>
                    <div className="mt-1"><StatusBadge status={viewHost.status} /></div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Registration #</Label>
                    <p className="text-sm font-mono">{viewHost.registration_number || "—"}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Registration Expires</Label>
                    <p className="text-sm">{viewHost.expires_at ? new Date(viewHost.expires_at).toLocaleDateString() : "—"}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Nights Used</Label>
                    <p className="text-sm">{viewHost.nights_used ?? 0} / {viewHost.night_limit ?? 90}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Registered</Label>
                    <p className="text-sm">{new Date(viewHost.created_at).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Host Type</Label>
                    <p className="text-sm capitalize">{(viewHost.host_type || "principal_residence").replace(/_/g, " ")}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Business Licence #</Label>
                    <p className="text-sm font-mono">{viewHost.business_licence_number || "—"}</p>
                  </div>
                  {viewHost.business_licence_number && (
                    <div className="col-span-2">
                      <Label className="text-xs text-muted-foreground">Business Licence Status</Label>
                      <p className={`text-sm font-medium capitalize ${
                        viewHost.business_licence_status === "revoked" ? "text-destructive" :
                        viewHost.business_licence_status === "valid" ? "text-success" :
                        viewHost.business_licence_status === "pending" ? "text-warning" :
                        "text-muted-foreground"
                      }`}>
                        {(viewHost.business_licence_status || "not_required").replace(/_/g, " ")}
                        {viewHost.business_licence_status === "revoked" && " — provincial registration will be cancelled"}
                      </p>
                    </div>
                  )}
                </div>

                {/* Registration Certificate */}
                <div className="col-span-2 pt-2 border-t space-y-2">
                  <Label className="text-xs text-muted-foreground">Registration Certificate</Label>
                  {!viewHostCert ? (
                    <p className="text-sm text-muted-foreground italic">No certificate uploaded yet</p>
                  ) : (
                    <div className="rounded-lg border bg-muted/40 px-3 py-2.5 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {viewHostCert.status === "approved" && <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />}
                          {viewHostCert.status === "rejected" && <XCircle className="h-4 w-4 text-red-500 shrink-0" />}
                          {viewHostCert.status === "pending"  && <AlertCircle className="h-4 w-4 text-amber-500 shrink-0" />}
                          <span className="text-xs truncate">{viewHostCert.file_name}</span>
                        </div>
                        <span className={`text-[10px] font-medium capitalize px-1.5 py-0.5 rounded-full ${
                          viewHostCert.status === "approved" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" :
                          viewHostCert.status === "rejected" ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" :
                          "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                        }`}>{viewHostCert.status}</span>
                      </div>
                      <div className="flex gap-2">
                        {viewHostCert.file_url && (
                          <a href={viewHostCert.file_url} target="_blank" rel="noopener noreferrer" className="flex-1">
                            <Button size="sm" variant="outline" className="w-full h-7 text-xs">View Document</Button>
                          </a>
                        )}
                        {viewHostCert.status !== "approved" && (
                          <Button size="sm" className="flex-1 h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                            disabled={updateDocStatus.isPending}
                            onClick={() => updateDocStatus.mutate({ id: viewHostCert.id, status: "approved" }, { onSuccess: () => refetchViewCert() })}>
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
                          </Button>
                        )}
                        {viewHostCert.status !== "rejected" && (
                          <Button size="sm" variant="outline" className="flex-1 h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                            disabled={updateDocStatus.isPending}
                            onClick={() => updateDocStatus.mutate({ id: viewHostCert.id, status: "rejected" }, { onSuccess: () => refetchViewCert() })}>
                            <XCircle className="h-3 w-3 mr-1" /> Reject
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setViewHost(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
